import { useCallback, useEffect } from "react";

import { useLocation, useNavigate } from "react-router-dom";

import {
  CARRIER_UPGRADE_RELATIVE_URL,
  COMMAND_LINK_LANDING_PATH_PREFIX,
  SHIPPER_UPGRADE_RELATIVE_URL,
} from "constants/index";

import { userBelongsToCompany } from "../utils";
import { useAuthContext } from "./useAuthContext";
import { useSubscriptionStatus } from "./useSubscriptionStatus";

/**
 * Sets up which path the user should be navigated to based on
 * their current state.
 */
export function useLoadPath() {
  const { isAuthenticated, user } = useAuthContext();
  const navigate = useNavigate();
  const { subscription } = useSubscriptionStatus();
  const location = useLocation();

  const redirectTo = useCallback(
    (url: string) => {
      if (window.location.pathname !== url) {
        navigate(url + window.location.hash);
      }
    },
    [navigate],
  );

  useEffect(() => {
    /**
     * A command-link landing is reached from an email with a signed token in the
     * query string, and the landing strips that token from the URL as soon as it
     * mounts. A redirect away from here is therefore not recoverable — coming back
     * lands on a tokenless URL, which renders "Invalid link" and discards whatever
     * the carrier had filled in.
     *
     * The redirects this hook exists for (pick a plan, set up a company) are still
     * reachable from the app itself, and nothing a landing offers is enforced by
     * them: whether the visitor may act is re-checked server-side on the way in and
     * again on submit, so the worst case is the page telling them why they cannot.
     */
    if (location.pathname.startsWith(COMMAND_LINK_LANDING_PATH_PREFIX)) {
      return;
    }
    if (isAuthenticated && user != null) {
      // Guard to prevent user to setting up company while already apart of company
      if (
        userBelongsToCompany(user) &&
        location.pathname === "/setup-company"
      ) {
        navigate("/");
        return;
      }

      const companySetupExemptPaths = ["/setup-company", "/account"];
      if (
        !userBelongsToCompany(user) &&
        !companySetupExemptPaths.includes(location.pathname)
      ) {
        navigate("/setup-company");
        return;
      }

      if (subscription == null) {
        return;
      }

      // A carrier with no subscription, and a shipper whose trial is over, both
      // need to pick a plan before the app is useful to them. Every other lapsed
      // subscription falls through and is told what happened by
      // LapsedSubscriptionBanner instead: those users used to be redirected to
      // /manage-inactive-subscription, a route deleted in PR #1021, so they
      // landed on the 404 page. The actions a lapsed company must not take are
      // gated server-side by RestrictionService, not by this redirect.
      if (subscription.subscription_plan === "CARRIER_NO_SUBSCRIPTION") {
        redirectTo(CARRIER_UPGRADE_RELATIVE_URL);
      } else if (
        subscription.subscription_plan === "SHIPPER_TRIAL" &&
        !subscription.is_active
      ) {
        redirectTo(SHIPPER_UPGRADE_RELATIVE_URL);
      }
    }
  }, [
    isAuthenticated,
    user,
    navigate,
    subscription,
    redirectTo,
    location.pathname,
  ]);
}
